import { Button } from '@blink/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, PhoneOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { ErrorAlert } from '@/components/common';
import { useI18n } from '@/i18n';
import { ApiClientError, api, type CallAnswer, type ParcelCalls } from '@/lib/api';

/** Opens the phone's dialler (a link click, so the Android app hands it to the phone). */
function openDialer(tel: string) {
  const a = document.createElement('a');
  a.href = tel;
  a.click();
}

/** Seconds left until `at`, ticking every second; 0 when the time has passed. */
function useSecondsUntil(at: string | null) {
  const left = () => (at ? Math.max(0, Math.ceil((new Date(at).getTime() - Date.now()) / 1000)) : 0);
  const [seconds, setSeconds] = useState(left);
  useEffect(() => {
    setSeconds(left());
    if (!at) return;
    const timer = setInterval(() => setSeconds(left()), 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [at]);
  return seconds;
}

const mmss = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

/**
 * In-app Call (plan 5.6): the server picks the number and logs the call; the next call on this
 * parcel is locked for a few minutes ("Call again in 04:12"). The number is never shown in the app.
 */
export function CallButtons({ shipmentId, calls, hasAlt }: { shipmentId: string; calls: ParcelCalls; hasAlt: boolean }) {
  const { t, f, formatTime } = useI18n();
  const queryClient = useQueryClient();
  const [lockedUntil, setLockedUntil] = useState<string | null>(calls.nextCallAt);
  useEffect(() => setLockedUntil(calls.nextCallAt), [calls.nextCallAt]);
  const call = useMutation({
    mutationFn: (number: 'AWB' | 'ALT') => api<CallAnswer>(`/courier/parcels/${shipmentId}/call`, { method: 'POST', body: { number } }),
    onSuccess: (answer) => {
      setLockedUntil(answer.nextCallAt);
      openDialer(answer.tel);
      // the parcel page and the proof-of-attempt screen both show the calls
      void queryClient.invalidateQueries({ queryKey: ['courier'] });
    },
    onError: (error) => {
      const next = error instanceof ApiClientError && error.code === 'CALL_LOCKED' ? (error.details as { nextCallAt?: string } | undefined)?.nextCallAt : undefined;
      if (next) setLockedUntil(next);
    },
  });
  const seconds = useSecondsUntil(lockedUntil);
  const locked = seconds > 0;
  const lockError = call.error instanceof ApiClientError && call.error.code === 'CALL_LOCKED';

  return (
    <div className="space-y-2" data-testid="call-box">
      {locked ? (
        <Button className="h-12 w-full text-base" variant="secondary" disabled data-testid="call-locked">
          <PhoneOff className="h-5 w-5" />
          {f(t.call.again, { time: mmss(seconds) })}
        </Button>
      ) : (
        <div className="flex gap-2">
          <Button className="h-12 flex-1 bg-success text-base text-white hover:bg-success/90" disabled={call.isPending} onClick={() => call.mutate('AWB')} data-testid="call">
            <Phone className="h-5 w-5" />
            {t.call.call}
          </Button>
          {hasAlt ? (
            <Button className="h-12 flex-1 text-base" variant="outline" disabled={call.isPending} onClick={() => call.mutate('ALT')} data-testid="call-alt">
              <Phone className="h-5 w-5" />
              {t.call.callAlt}
            </Button>
          ) : null}
        </div>
      )}
      {lockError ? null : <ErrorAlert error={call.error} />}
      <p className="text-xs text-muted-foreground" data-testid="call-count">
        {calls.list.length
          ? f(t.call.made, { count: calls.list.length, times: calls.list.map((c) => formatTime(c.at)).join(' · ') })
          : f(t.call.rule, { minutes: calls.lockMinutes })}
      </p>
    </div>
  );
}
